import type { ReferenceCardMap } from '~/utils/article/referenceUtils'
import { injectChevronIntoAccordionHeaders, toggleAccordion } from '~/utils/article/accordionUtils'
import { createReferenceCard, createReferenceCardMap, injectSectionIconAfterReferenceButtons } from '~/utils/article/referenceUtils'
import { getCurrentTooltipState, hideTooltip, isTooltipPinnedForElement, setAnchorAttrs, setTooltipTimer, showTooltip, toggleTooltipPin } from '~/utils/article/tooltipUtils'

export function useArticleInteractions(addLawNodeIdToReferences: (id: string) => void, removeLawIdFromReferences: (id: string) => void, lawReferenceNodeIds: Ref<string[]>, onReferenceOpen?: (trigger: HTMLElement) => void) {
  const contentRoot = ref<HTMLElement | null>(null)
  const refCardMap: ReferenceCardMap = createReferenceCardMap()
  let tooltipTimer: number | null = null

  const handleBodyClick = (e: MouseEvent) => {
    const target = e.target as Element

    // Handle reference buttons
    const buttonRef = target.closest('button.reference')
    if (buttonRef) {
      const id = buttonRef.getAttribute('data-id')
      if (id) {
        // Desktop closes the reference on re-click. Mobile/tablet re-clicks open the reference view again, close from the card button
        const isAlreadyReferenced = lawReferenceNodeIds.value.includes(id)
        if (isAlreadyReferenced && isDesktopViewport()) {
          removeLawIdFromReferences(id)
        }
        else {
          if (!isAlreadyReferenced) {
            addLawNodeIdToReferences(id)
          }
          onReferenceOpen?.(buttonRef as HTMLElement)
        }
      }
      e.stopPropagation()
      return
    }

    // Handle reference spans
    const ref = target.closest('span.reference') as HTMLElement | null
    if (ref) {
      const first = ref.querySelector(':scope > span:first-child') as HTMLElement | null
      if (first && first.contains(target)) {
        const details = ref.querySelector(':scope > span:nth-child(2)') as HTMLElement | null
        if (!details) {
          return
        }

        const existing = refCardMap.getCard(ref)
        if (existing) {
          refCardMap.removeCard(ref)
        }
        else {
          const card = createReferenceCard(ref, details)

          // Add close button event listener
          const closeBtn = card.querySelector('.reference-card-close')
          if (closeBtn) {
            closeBtn.addEventListener('click', (ev) => {
              ev.preventDefault()
              ev.stopPropagation()
              refCardMap.removeCard(ref)
            })
          }

          // Insert immediately after the ref span
          if (ref.parentNode) {
            ref.parentNode.insertBefore(card, ref.nextSibling)
          }
          refCardMap.addCard(ref, card)
        }
      }
      e.stopPropagation()
      return
    }

    // Handle information icon clicks for tooltip pinning
    const info = target.closest('.information') as HTMLElement | null
    if (info) {
      const icon = info.querySelector(':scope > span:first-child') as HTMLElement | null
      const tip = info.querySelector(':scope > span:nth-child(2)') as HTMLElement | null

      if (icon && tip && icon.contains(target)) {
        // Cancel a pending hover-show timer so it doesn't fire after the click
        // and replace the freshly pinned tooltip with a hover one.
        if (tooltipTimer) {
          clearTimeout(tooltipTimer)
          tooltipTimer = null
        }
        const text = tip.textContent || ''
        toggleTooltipPin(icon, text)
        e.stopPropagation()
        return
      }
    }

    // Handle accordion headers
    const header = target.closest('.accordion')
    if (header) {
      toggleAccordion(header)
      e.stopPropagation()
    }
  }

  const handleMouseOver = (e: MouseEvent) => {
    const t = e.target as Element
    const info = t.closest('.information') as HTMLElement | null
    if (!info) {
      return
    }
    const icon = info.querySelector(':scope > span:first-child') as HTMLElement | null
    const tip = info.querySelector(':scope > span:nth-child(2)') as HTMLElement | null
    if (!icon || !tip) {
      return
    }
    if (!icon.contains(t)) {
      return
    }

    if (isTooltipPinnedForElement(icon)) {
      return
    }

    if (tooltipTimer) {
      clearTimeout(tooltipTimer)
    }
    tooltipTimer = window.setTimeout(() => {
      showTooltip(icon, tip.textContent || '', 'hover')
      if (tooltipTimer) {
        setTooltipTimer(tooltipTimer)
      }
    }, 200)
  }

  const handleMouseOut = (e: MouseEvent) => {
    const t = e.target as Element
    const info = t.closest('.information') as HTMLElement | null
    if (!info) {
      return
    }

    const icon = info.querySelector(':scope > span:first-child') as HTMLElement | null
    if (icon && isTooltipPinnedForElement(icon)) {
      return
    }

    const related = e.relatedTarget as Element | null
    const stillInside = related !== null && related.closest !== undefined && related.closest('.information') === info
    if (!stillInside) {
      // Cancel any pending show-timer so a tooltip doesn't appear after the mouse has already left.
      if (tooltipTimer) {
        clearTimeout(tooltipTimer)
        tooltipTimer = null
      }
      hideTooltip()
    }
  }

  const handleScroll = () => {
    if (tooltipTimer) {
      clearTimeout(tooltipTimer)
      tooltipTimer = null
    }
    const state = getCurrentTooltipState()
    if (state?.type === 'hover') {
      hideTooltip()
    }
  }

  const initializeContent = () => {
    const root = contentRoot.value
    if (!root) {
      return
    }
    setAnchorAttrs(root)
    injectSectionIconAfterReferenceButtons(root, lawReferenceNodeIds.value)
    injectChevronIntoAccordionHeaders(root)
  }

  let scrollTarget: HTMLElement | Window | null = null

  const findScrollTarget = (from: HTMLElement): HTMLElement | Window => {
    let node: HTMLElement | null = from.parentElement
    while (node) {
      const style = getComputedStyle(node)
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return node
      }
      node = node.parentElement
    }
    return window
  }

  onMounted(() => {
    const root = contentRoot.value
    if (root) {
      root.addEventListener('click', handleBodyClick)
      root.addEventListener('mouseover', handleMouseOver)
      root.addEventListener('mouseout', handleMouseOut)
      initializeContent()
      scrollTarget = findScrollTarget(root)
      scrollTarget.addEventListener('scroll', handleScroll, { passive: true })
    }
  })

  // Watch for changes in lawReferenceNodeIds and re-inject icons when references change
  watch(lawReferenceNodeIds, (newRefs) => {
    const root = contentRoot.value
    if (root) {
      injectSectionIconAfterReferenceButtons(root, newRefs)
    }
  }, { immediate: true })

  onBeforeUnmount(() => {
    const root = contentRoot.value
    if (root) {
      root.removeEventListener('click', handleBodyClick)
      root.removeEventListener('mouseover', handleMouseOver)
      root.removeEventListener('mouseout', handleMouseOut)
    }
    if (scrollTarget) {
      scrollTarget.removeEventListener('scroll', handleScroll)
      scrollTarget = null
    }
    if (tooltipTimer) {
      clearTimeout(tooltipTimer)
      tooltipTimer = null
    }
    hideTooltip()
  })

  return {
    contentRoot,
    refCardMap,
  }
}
